import { useState } from "react";
import { Reveal, ChapterTag } from "./motion";
import { useStore } from "../../context/StoreContext";
import { MoveHorizontal, Play } from "lucide-react";

export const Demo = () => {
  const { config } = useStore();
  const media = config?.media || {};
  const [pos, setPos] = useState(50);
  const before = media.demo_before || media.problem_sofa;
  const after = media.demo_after || media.hero_image;

  return (
    <section id="demo" data-testid="demo-section" className="py-16 sm:py-24 lg:py-32 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterTag num="03" label="Demo" />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-balance">
            One pass. See the difference.
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <div
              data-testid="before-after-slider"
              className="relative rounded-[2rem] overflow-hidden aspect-[4/3] bg-zinc-200 ring-1 ring-zinc-900/5 select-none"
            >
              {after && <img src={after} alt="After using PeloCero" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
              {before && (
                <img
                  src={before}
                  alt="Before using PeloCero"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
                />
              )}
              <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }}>
                <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center">
                  <MoveHorizontal className="w-5 h-5 text-ink" />
                </span>
              </div>
              <input
                data-testid="before-after-range"
                type="range"
                min="5"
                max="95"
                value={pos}
                onChange={(e) => setPos(Number(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                aria-label="Compare before and after"
              />
              <span className="absolute top-4 left-4 bg-ink/70 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur">BEFORE</span>
              <span className="absolute top-4 right-4 bg-brand text-white text-xs font-semibold px-3 py-1.5 rounded-full">AFTER</span>
            </div>
          </Reveal>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg text-zinc-600 leading-relaxed" data-testid="demo-explanation">
                Swipe it back and forth over the surface. The system collects the hair inside its compartment. When you're done, open the bin, empty it and use it again.
              </p>
              <ol className="mt-8 space-y-4">
                {["Swipe back and forth", "Hair collects in the bin", "Open, empty and repeat"].map((step, i) => (
                  <li key={step} className="flex items-center gap-4">
                    <span className="w-8 h-8 rounded-full bg-white ring-1 ring-zinc-200 flex items-center justify-center font-mono-brand text-xs text-brand font-medium shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm sm:text-base font-medium">{step}</span>
                  </li>
                ))}
              </ol>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 rounded-2xl overflow-hidden bg-ink aspect-video flex items-center justify-center ring-1 ring-zinc-900/10" data-testid="demo-video-frame">
                {media.demo_video ? (
                  <video src={media.demo_video} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-white/70 px-6">
                    <Play className="w-8 h-8 mx-auto mb-3 text-brand" />
                    <p className="text-sm">Demo video</p>
                    <p className="text-xs text-white/40 mt-1">Upload it from the settings panel</p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
